import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Select,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { useQuery } from '@tanstack/react-query'
import { IconCircleCheck, IconSend } from '@tabler/icons-react'
import { DatePickerInput } from '@mantine/dates'
import { listCatalogs, listSchemas, listTables } from '../../api/databricks'
import { formatDate, REQUEST_RECIPIENT } from './intake'
import {
  buildDataQualityOutlookUrl,
  DATA_QUALITY_INITIAL,
  type DataQualityValues,
} from './dataQuality'

const required = (message: string) => (v: string) =>
  String(v ?? '').trim().length === 0 ? message : null

const MEDALLION_CATALOGS = ['bronze', 'silver', 'gold']

export function DataQualityForm() {
  const [submitted, setSubmitted] = useState(false)

  const form = useForm<DataQualityValues>({
    initialValues: DATA_QUALITY_INITIAL,
    validate: {
      catalog: required('Catalog is required'),
      schema: required('Schema is required'),
      table: required('Table is required'),
      issueDescription: required('Issue Description is required'),
      expectedResult: required('Expected Result is required'),
      actualResult: required('Actual Result is required'),
      dateFirstObserved: required('Date Issue First Observed is required'),
      affectedReports: required('Affected Reports is required'),
      businessImpact: required('Business Impact is required'),
    },
  })

  // Cascading catalog > schema > table queries.
  // Catalogs and schemas rarely change; cache for 30 minutes so switching
  // tabs or re-rendering doesn't re-fetch.
  const THIRTY_MINUTES = 30 * 60 * 1000

  const catalogsQuery = useQuery({
    queryKey: ['databricks', 'uc', 'catalogs'],
    queryFn: listCatalogs,
    staleTime: THIRTY_MINUTES,
  })

  const schemasQuery = useQuery({
    queryKey: ['databricks', 'uc', 'schemas', form.values.catalog],
    queryFn: () => listSchemas(form.values.catalog),
    enabled: Boolean(form.values.catalog),
    staleTime: THIRTY_MINUTES,
  })

  const tablesQuery = useQuery({
    queryKey: ['databricks', 'uc', 'tables', form.values.catalog, form.values.schema],
    queryFn: () => listTables(form.values.catalog, form.values.schema),
    enabled: Boolean(form.values.catalog) && Boolean(form.values.schema),
    staleTime: THIRTY_MINUTES,
  })

  const catalogOptions = useMemo(
    () =>
      catalogsQuery.data
        ?.map((c) => c.name)
        .filter((name) =>
          MEDALLION_CATALOGS.some((m) => name.toLowerCase().startsWith(m))
        ) ?? [],
    [catalogsQuery.data]
  )

  const schemaOptions = useMemo(
    () => schemasQuery.data?.map((s) => s.name) ?? [],
    [schemasQuery.data]
  )

  const tableOptions = useMemo(
    () => tablesQuery.data?.map((t) => t.name) ?? [],
    [tablesQuery.data]
  )

  const handleSubmit = form.onSubmit((values) => {
    const loginHint = localStorage.getItem('prism_username')
    window.open(
      buildDataQualityOutlookUrl(values, loginHint),
      '_blank',
      'noopener,noreferrer'
    )
    setSubmitted(true)
  })

  return (
    <Box>
      <Text size="sm" c="gray.6" mb="md">
        Report data accuracy, completeness, reconciliation, or consistency
        issues requiring investigation and remediation. Submitting opens an
        Outlook email to {REQUEST_RECIPIENT}.
      </Text>

      {submitted && (
        <Alert
          color="green"
          icon={<IconCircleCheck size={16} />}
          title="Email drafted in Outlook"
          mb="md"
          withCloseButton
          onClose={() => setSubmitted(false)}
        >
          A &quot;Prism Portal Request - Data Quality Issue&quot; email to{' '}
          {REQUEST_RECIPIENT} should have opened in an Outlook compose tab.
          Review and send it to complete your request.
        </Alert>
      )}

      <Card shadow="sm" withBorder>
        <Box p="md">
          <form onSubmit={handleSubmit}>
            <Group grow mb="md">
              <Select
                label="Catalog (Required)"
                placeholder={catalogsQuery.isLoading ? 'Loading...' : 'Select catalog'}
                data={catalogOptions}
                searchable
                clearable
                value={form.values.catalog || null}
                onChange={(value) => {
                  form.setFieldValue('catalog', value ?? '')
                  form.setFieldValue('schema', '')
                  form.setFieldValue('table', '')
                }}
                error={form.errors.catalog}
              />
              <Select
                label="Schema (Required)"
                placeholder={
                  !form.values.catalog
                    ? 'Select a catalog first'
                    : schemasQuery.isLoading
                      ? 'Loading...'
                      : 'Select schema'
                }
                data={schemaOptions}
                searchable
                clearable
                disabled={!form.values.catalog}
                value={form.values.schema || null}
                onChange={(value) => {
                  form.setFieldValue('schema', value ?? '')
                  form.setFieldValue('table', '')
                }}
                error={form.errors.schema}
              />
              <Select
                label="Table (Required)"
                placeholder={
                  !form.values.schema
                    ? 'Select a schema first'
                    : tablesQuery.isLoading
                      ? 'Loading...'
                      : 'Select table'
                }
                data={tableOptions}
                searchable
                clearable
                disabled={!form.values.schema}
                value={form.values.table || null}
                onChange={(value) => form.setFieldValue('table', value ?? '')}
                error={form.errors.table}
              />
            </Group>

            <Textarea
              label="Issue Description (Required)"
              placeholder="Describe the data quality issue"
              minRows={3}
              autosize
              mb="md"
              {...form.getInputProps('issueDescription')}
            />

            <Textarea
              label="Transformation Logic"
              placeholder="Relevant transformation or business rule"
              minRows={2}
              autosize
              mb="md"
              {...form.getInputProps('transformationLogic')}
            />

            <Group grow mb="md">
              <Textarea
                label="Expected Result (Required)"
                placeholder="What the data should look like"
                minRows={2}
                autosize
                {...form.getInputProps('expectedResult')}
              />
              <Textarea
                label="Actual Result (Required)"
                placeholder="What the data actually looks like"
                minRows={2}
                autosize
                {...form.getInputProps('actualResult')}
              />
            </Group>

            <Group grow mb="md">
              <DatePickerInput
                label="Date Issue First Observed (Required)"
                placeholder="Pick a date"
                valueFormat="M/D/YYYY"
                value={form.values.dateFirstObserved ? new Date(form.values.dateFirstObserved) : new Date()}
                onChange={(d) => form.setFieldValue('dateFirstObserved', d ? formatDate(d) : '')}
                error={form.errors.dateFirstObserved}
              />
              <TextInput
                label="Affected Reports (Required)"
                placeholder="Dashboards, reports, or downstream consumers"
                {...form.getInputProps('affectedReports')}
              />
            </Group>

            <Textarea
              label="Business Impact (Required)"
              placeholder="How does this issue affect business operations?"
              minRows={2}
              autosize
              mb="md"
              {...form.getInputProps('businessImpact')}
            />

            <Textarea
              label="Sample Records"
              placeholder="Paste sample rows or record IDs showing the issue"
              minRows={2}
              autosize
              mb="md"
              {...form.getInputProps('sampleRecords')}
            />

            <Divider my="md" />
            <Group justify="flex-end">
              <Button
                variant="light"
                color="gray"
                onClick={() => {
                  form.reset()
                  setSubmitted(false)
                }}
              >
                Clear
              </Button>
              <Button
                type="submit"
                color="prism"
                leftSection={<IconSend size={16} />}
              >
                Submit Request
              </Button>
            </Group>
          </form>
        </Box>
      </Card>
    </Box>
  )
}
